// src/components/sidebar/sidebar.hooks.ts (USER APP)
import { useCallback, useEffect, useRef, useState } from "react";
import { useAuth } from "../../../contexts/auth/auth-context";
import { userNotesApi } from "../../../api/notes/notes";
import { NoteMetaResponse } from "../../../api/notes/notes.types";
import { searchApi } from "../../../api/search/search";
import type { SearchNoteResult, SearchConversationResult } from "../../../api/search/search.types";
import type { SettingsTab } from "../../chat/settings/settings.hooks";

export const formatUpdatedAt = (iso: string) => {
    if (!iso) return "";
    const d = new Date(iso);
    const now = new Date();

    const toMidnight = (dt: Date) =>
        new Date(dt.getFullYear(), dt.getMonth(), dt.getDate());
    const dMid = toMidnight(d);
    const today = toMidnight(now);
    const yest = new Date(today);
    yest.setDate(today.getDate() - 1);

    const sameDay = (a: Date, b: Date) => a.getTime() === b.getTime();

    if (sameDay(dMid, today)) return "Today";
    if (sameDay(dMid, yest)) return "Yesterday";

    const sameYear = d.getFullYear() === now.getFullYear();
    return new Intl.DateTimeFormat(undefined, {
        day: "2-digit",
        month: "short",
        ...(sameYear ? {} : { year: "numeric" }),
    }).format(d);
};

/* ───── Persistent notes cache (stale-while-revalidate) ───── */
let _notesCache: NoteMetaResponse[] | null = null;
let _fetchInFlight = false;

const sortNotes = (data: unknown): NoteMetaResponse[] =>
    (Array.isArray(data) ? data : []).sort(
        (a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime(),
    );

/** Fetch notes and store in the persistent cache. Safe to call multiple times. */
export const prefetchNotesMeta = () => {
    if (_fetchInFlight) return;
    _fetchInFlight = true;
    userNotesApi
        .listNotes()
        .then((data) => { _notesCache = sortNotes(data); })
        .catch(() => { /* leave cache as-is on error */ })
        .finally(() => { _fetchInFlight = false; });
};

/** Read the cached notes (may be null if prefetch hasn't completed yet). */
const getCachedNotes = (): NoteMetaResponse[] | null => _notesCache;

/** Update the persistent cache (called after mutations or background refresh). */
const updateNotesCache = (notes: NoteMetaResponse[]) => { _notesCache = notes; };

/** Invalidate the cache so the next sidebar open triggers a fresh fetch. */
export const invalidateNotesCache = () => { _notesCache = null; };

export const useSidebar = (open: boolean) => {
    const { signOut, profile } = useAuth();
    const [showSettings, setShowSettings] = useState(false);
    const [searchActive, setSearchActive] = useState(false);
    const [showFiles, setShowFiles] = useState(false);

    // Which tab Settings should open to
    const [settingsInitialTab, setSettingsInitialTab] = useState<SettingsTab>(
        "profile",
    );

    // Notes meta (user-visible scope)
    const [notesMeta, setNotesMeta] = useState<NoteMetaResponse[]>([]);
    const [isLoadingNotesMeta, setIsLoadingNotesMeta] = useState(false);

    // Selected note for the Notes screen
    const [selectedNoteId, setSelectedNoteId] = useState<string | null>(null);

    const displayName = [profile?.first_name, profile?.last_name].filter(Boolean).join(" ") || profile?.nickname || "";
    const avatarUrl = "";
    const planName = "Starter Plan"; // TODO: wire to billing context when available

    const bodyRef = useRef<HTMLDivElement>(null);
    const barRef = useRef<HTMLDivElement>(null);
    const [sheetTop, setSheetTop] = useState<number>(36);

    useEffect(() => {
        const measure = () => {
            if (!bodyRef.current || !barRef.current) return;
            const bodyRect = bodyRef.current.getBoundingClientRect();
            const barRect = barRef.current.getBoundingClientRect();
            setSheetTop(barRect.bottom - bodyRect.top);
        };

        measure();
        window.addEventListener("resize", measure);
        return () => window.removeEventListener("resize", measure);
    }, [open]);

    // ── Unified search state ──
    const [searchQuery, setSearchQuery] = useState("");
    const [searchNotes, setSearchNotes] = useState<SearchNoteResult[]>([]);
    const [searchConversations, setSearchConversations] = useState<SearchConversationResult[]>([]);
    const [searchLoading, setSearchLoading] = useState(false);
    const searchTimerRef = useRef<number | null>(null);
    const searchAbortRef = useRef<AbortController | null>(null);

    useEffect(() => {
        if (!open) {
            setSearchActive(false);
            setSearchQuery("");
            setSearchNotes([]);
            setSearchConversations([]);
            setSearchLoading(false);
            if (searchTimerRef.current) {
                window.clearTimeout(searchTimerRef.current);
                searchTimerRef.current = null;
            }
            if (searchAbortRef.current) {
                searchAbortRef.current.abort();
                searchAbortRef.current = null;
            }
        }
    }, [open]);

    // Ref to compare incoming data — skip setState when unchanged to avoid re-renders
    const notesMetaRef = useRef<NoteMetaResponse[]>([]);

    const refreshNotesMeta = useCallback(async () => {
        try {
            const data = await userNotesApi.listNotes();
            const sorted = sortNotes(data);

            // Only update state if the data actually changed
            const prev = notesMetaRef.current;
            const changed =
                sorted.length !== prev.length ||
                sorted.some((n, i) => n.id !== prev[i]?.id || n.title !== prev[i]?.title || n.updated_at !== prev[i]?.updated_at);

            if (changed) {
                notesMetaRef.current = sorted;
                setNotesMeta(sorted);
            }
            // Always keep persistent cache in sync
            updateNotesCache(sorted);
        } catch (err) {
            console.error("Failed to load notes meta", err);
        }
    }, []);

    // Load notes list when sidebar opens — stale-while-revalidate
    useEffect(() => {
        if (!open) return;

        const cached = getCachedNotes();

        if (cached) {
            // Show cached data instantly (no spinner)
            notesMetaRef.current = cached;
            setNotesMeta(cached);
            setIsLoadingNotesMeta(false);
            // Revalidate in background — refreshNotesMeta updates state only if data changed
            void refreshNotesMeta();
            return;
        }

        // No cache yet (first ever open) — show spinner and fetch
        let alive = true;
        setIsLoadingNotesMeta(true);

        const run = async () => {
            try {
                const data = await userNotesApi.listNotes();
                if (!alive) return;
                const sorted = sortNotes(data);
                notesMetaRef.current = sorted;
                setNotesMeta(sorted);
                updateNotesCache(sorted);
            } catch (err) {
                console.error("Failed to load notes meta", err);
                if (!alive) return;
                setNotesMeta([]);
                notesMetaRef.current = [];
            } finally {
                if (alive) setIsLoadingNotesMeta(false);
            }
        };

        void run();
        return () => { alive = false; };
    }, [open, refreshNotesMeta]);


    // Listen for open-settings-billing event (e.g. from TrialExpiredScreen)
    useEffect(() => {
        const handleOpenBillingEvent = () => {
            setSettingsInitialTab("billing");
            setShowSettings(true);
        };
        window.addEventListener("open-settings-billing", handleOpenBillingEvent);
        return () => window.removeEventListener("open-settings-billing", handleOpenBillingEvent);
    }, []);

    const handleOpenSettings = (tab: SettingsTab = "profile") => {
        setSettingsInitialTab(tab);
        setShowSettings(true);
    };

    const handleCloseSettings = () => setShowSettings(false);

    const handleOpenFiles = () => setShowFiles(true);
    const handleCloseFiles = () => setShowFiles(false);

    const handleLogout = async () => {
        await signOut();
    };

    const handleBilling = () => {
        handleOpenSettings("billing");
    };

    const handleNotifications = () => {
        handleOpenSettings("notifications");
    };

    const openNewNote = useCallback(() => {
        setSelectedNoteId(null);
    }, []);

    const openExistingNote = useCallback((noteId: string) => {
        setSelectedNoteId(noteId);
    }, []);

    const onNoteSaved = useCallback(() => {
        // After create/update, silently refresh meta so sidebar shows latest updated_at/title
        void refreshNotesMeta();
    }, [refreshNotesMeta]);

    const onNoteDeleted = useCallback(() => {
        // Optimistically remove from local list immediately
        const deletedId = selectedNoteId;
        setSelectedNoteId(null);
        if (deletedId) {
            setNotesMeta((prev) => prev.filter((n) => n.id !== deletedId));
            notesMetaRef.current = notesMetaRef.current.filter((n) => n.id !== deletedId);
            updateNotesCache(notesMetaRef.current);
        }
        // Still refresh to get canonical server state
        void refreshNotesMeta();
    }, [refreshNotesMeta, selectedNoteId]);

    // ── Search handler (called from SearchBar onSearchChange) ──
    const handleSearchChange = useCallback((value: string) => {
        setSearchQuery(value);
        const trimmed = value.trim();

        // Cancel pending timer + in-flight request
        if (searchTimerRef.current) {
            window.clearTimeout(searchTimerRef.current);
            searchTimerRef.current = null;
        }
        if (searchAbortRef.current) {
            searchAbortRef.current.abort();
            searchAbortRef.current = null;
        }

        if (!trimmed || trimmed.length < 2) {
            setSearchNotes([]);
            setSearchConversations([]);
            setSearchLoading(false);
            return;
        }

        setSearchLoading(true);
        const controller = new AbortController();
        searchAbortRef.current = controller;

        searchTimerRef.current = window.setTimeout(async () => {
            try {
                const result = await searchApi.search(trimmed, 10);
                if (controller.signal.aborted) return;
                setSearchNotes(result.notes);
                setSearchConversations(result.conversations);
            } catch (err) {
                if (controller.signal.aborted) return;
                console.error("Sidebar search failed:", err);
                setSearchNotes([]);
                setSearchConversations([]);
            } finally {
                if (!controller.signal.aborted) {
                    setSearchLoading(false);
                }
            }
        }, 300);
    }, []);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            if (searchTimerRef.current) window.clearTimeout(searchTimerRef.current);
            if (searchAbortRef.current) searchAbortRef.current.abort();
        };
    }, []);

    return {
        showSettings,
        setShowSettings,
        searchActive,
        setSearchActive,
        showFiles,
        setShowFiles,
        displayName,
        avatarUrl,
        planName,
        bodyRef,
        barRef,
        sheetTop,
        onSettings: () => handleOpenSettings("profile"),
        onCloseSettings: handleCloseSettings,
        onDocs: handleOpenFiles,
        onCloseDocs: handleCloseFiles,
        onLogout: handleLogout,
        onBilling: handleBilling,
        onNotifications: handleNotifications,
        settingsInitialTab,

        // Notes (user)
        notesMeta,
        isLoadingNotesMeta,
        selectedNoteId,
        openNewNote,
        openExistingNote,
        onNoteSaved,
        onNoteDeleted,
        reloadNotesMeta: refreshNotesMeta,

        // Search
        searchQuery,
        searchNotes,
        searchConversations,
        searchLoading,
        handleSearchChange,
    };
};
